import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { RAINVIEWER_COLOR_SCHEME, type RadarTileOptions } from '@repo/shared';
import { DEFAULT_BASEMAP_ID, basemapDefaults, basemapIdSchema, type BasemapId } from './basemap';
import { readPersisted, writePersisted } from './persisted';

/**
 * View preferences, persisted in this browser.
 *
 * These are the knobs that describe *how someone reads the map* — which
 * basemap, how hard it's pushed back, how the radar is coloured. Re-setting
 * them on every visit is the kind of small friction that makes a tool feel
 * disposable, so they survive a reload.
 *
 * Nothing here is a secret and nothing leaves the browser. Everything read back
 * is re-validated: see `persisted.ts`.
 */

/** Versioned so a future shape change can be migrated rather than guessed at. */
const STORAGE_KEY = 'raydario.preferences.v2';

export const preferencesSchema = z.object({
  basemap: basemapIdSchema,
  /** 0–1. Seeded from the basemap's own default, then owned by the viewer. */
  basemapDim: z.number().min(0).max(1),
  /** 0–1. Zero is allowed on purpose — "hide the radar" is a real request. */
  radarOpacity: z.number().min(0).max(1),
  showCoverage: z.boolean(),
  /** Interpolate between reflectivity steps instead of hard banding. */
  smooth: z.boolean(),
  /** Render frozen precipitation in its own palette. */
  showSnow: z.boolean(),
});
export type Preferences = z.infer<typeof preferencesSchema>;

export const DEFAULT_PREFERENCES: Preferences = {
  basemap: DEFAULT_BASEMAP_ID,
  basemapDim: basemapDefaults(DEFAULT_BASEMAP_ID).dim,
  radarOpacity: 0.8,
  // On by default: the failure this prevents is silent. A viewer in a coverage
  // gap sees a clear map and reads it as clear weather, and nothing about the
  // radar layer alone would ever correct them.
  showCoverage: true,
  smooth: true,
  showSnow: true,
};

export function usePreferences() {
  const [preferences, setPreferences] = useState<Preferences>(DEFAULT_PREFERENCES);

  // Read after mount rather than during render: localStorage is unavailable
  // during SSR/tests and reading it in an initializer would be a side effect.
  useEffect(() => {
    setPreferences(readPersisted(STORAGE_KEY, preferencesSchema, DEFAULT_PREFERENCES));
  }, []);

  const update = useCallback((patch: Partial<Preferences>) => {
    setPreferences((current) => {
      const next = { ...current, ...patch };
      writePersisted(STORAGE_KEY, next);
      return next;
    });
  }, []);

  /**
   * Switching basemap re-seeds the dim, because the right amount of dimming is
   * a property of the imagery underneath rather than a taste the viewer carries
   * from one basemap to the next.
   */
  const setBasemap = useCallback(
    (basemap: BasemapId) => update({ basemap, basemapDim: basemapDefaults(basemap).dim }),
    [update],
  );

  const reset = useCallback(() => {
    writePersisted(STORAGE_KEY, DEFAULT_PREFERENCES);
    setPreferences(DEFAULT_PREFERENCES);
  }, []);

  return { preferences, update, setBasemap, reset };
}

/** The subset of preferences that addresses a radar tile. */
export function tileOptionsFrom(preferences: Preferences): RadarTileOptions {
  return {
    // Not a preference: RainViewer returns the same tile for every scheme id it
    // documents, so there is nothing here for a viewer to choose between.
    colorScheme: RAINVIEWER_COLOR_SCHEME,
    smooth: preferences.smooth,
    showSnow: preferences.showSnow,
    // RainViewer serves 256px and 512px pyramids; 512 is crisper on retina and
    // is not something a viewer has an opinion about.
    tileSize: 512,
  };
}
